import { Outlet, useMatches } from 'react-router';

import { SidePanelHost } from '@/shared/components/side-panel';
import { cn } from '@/shared/lib/cn';
import { useRealtimeStatus } from '@/shared/realtime';

import { CommandPalette } from './CommandPalette';
import { ConnectionBanner } from './ConnectionBanner';
import { MenuCustomizeDialog } from './MenuCustomizeDialog';
import { MobileNav } from './MobileNav';
import { PasswordChangeBanner } from './PasswordChangeBanner';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/** Route `handle` understood by the shell. `layout: 'full'` removes the page padding / max width (boards, chat). */
export interface ShellRouteHandle {
  layout?: 'default' | 'full';
}

function useFullBleed(): boolean {
  const matches = useMatches();
  return matches.some((m) => (m.handle as ShellRouteHandle | undefined)?.layout === 'full');
}

/** Authenticated layout: sidebar, top bar, content card, side panels and the ⌘K palette. */
export function AppShell() {
  const fullBleed = useFullBleed();
  // Exposed for diagnostics and the E2E suite (realtime connected, no reconnect loop).
  const realtimeStatus = useRealtimeStatus();
  return (
    <div className="flex min-h-dvh bg-surface" data-realtime-status={realtimeStatus}>
      <Sidebar />
      <MobileNav />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main
          id="main"
          className="flex min-w-0 flex-1 flex-col border-y bg-background sm:rounded-none lg:mr-2 lg:mb-2 lg:rounded-xl lg:border lg:shadow-xs"
        >
          <div
            className={cn(
              'flex min-w-0 flex-1 flex-col',
              fullBleed ? 'p-0' : 'mx-auto w-full max-w-[90rem] px-4 py-6 sm:px-6 lg:px-10 lg:py-8',
            )}
          >
            <ConnectionBanner />
            <PasswordChangeBanner />
            <Outlet />
          </div>
        </main>
      </div>
      <SidePanelHost />
      <CommandPalette />
      <MenuCustomizeDialog />
    </div>
  );
}
